import React, { useEffect } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Field, Formik } from "formik";
import {
  useAddTemplateMutation,
  useTemplateListQuery,
} from "../../../graphql/template.hooks";
import { ChevronLeft, InfoCircle } from "react-bootstrap-icons";
import { createTemplateSchema } from "../validation/createTemplate";
import { useUserQuery } from "../../../graphql/menu.hooks";
import { useTemplateCategoryListQuery } from "../../../graphql/templateCategory.hooks";
import { StyledInput } from "../../../components/inputs/styledInput";

export const CreateTemplate = () => {
  const navigate = useNavigate();
  const [createTemplate, { loading: createLoading }] = useAddTemplateMutation();
  const { data: templateCategoryList, loading: categoryLoading } =
    useTemplateCategoryListQuery();
  const { data: user, loading: userLoading } = useUserQuery();
  const { refetch: refetchTemplates } = useTemplateListQuery({
    variables: { userId: user?.user.id },
  });

  useEffect(() => {
    document.title = "New Template";
  }, []);

  interface IinitialValues {
    type: number | null;
    name: string;
    content: string;
    isCommon: boolean;
    templateCategory: number | null;
    user: number | null;
  }

  const initialValues: IinitialValues = {
    name: "",
    content: "",
    isCommon: false,
    type: null,
    templateCategory: null,
    user: null,
  };

  const tryCreateTemplate = async (values: any) => {
    try {
      const { data } = await createTemplate({
        variables: {
          ...values,
          type: +values.type,
          templateCategory: +values.templateCategory,
          user: +values.user,
        },
      });
      if (data.adminAddTemplate.id) {
        refetchTemplates();
        backToList();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const backToList = () => {
    navigate("/template");
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="mb-4">
          <h1>Create New Template</h1>
          <div
            onClick={backToList}
            className="d-flex align-items-center"
            style={{ cursor: "pointer" }}
          >
            <ChevronLeft className="me-1" size={18} color="#3A83F6" />
            <p className="text-primary m-0">To All Templates</p>
          </div>
        </div>
      </div>
      <div className="g-4 py-5 d-flex flex-column justify-content-center align-items-center bg-white rounded">
        <div className="d-flex flex-column flex-md-row justify-content-between col-11 col-md-8">
          <Formik
            initialValues={initialValues}
            validationSchema={createTemplateSchema}
            onSubmit={tryCreateTemplate}
          >
            {({ handleSubmit, touched, errors, values }) => (
              <Form className="w-100">
                <div className="form-floating mb-3 w-100">
                  <StyledInput
                    name="name"
                    label="Template Name"
                    error={!!(touched.name && errors.name)}
                  />
                </div>
                <div className="form-floating mb-3 w-100">
                  <StyledInput
                    name="content"
                    as="textarea"
                    style={{ resize: "none", height: "350px" }}
                    label="Content"
                    error={!!(touched.content && errors.content)}
                  />
                </div>
                <div className="d-flex align-items-center mb-3 w-100">
                  <Field
                    name="isCommon"
                    type="checkbox"
                    className="form-check-input m-0"
                    id="common"
                  />
                  <label
                    htmlFor="isCommon"
                    className="form-label text-dark ps-2 m-0"
                  >
                    Common
                  </label>
                  <span className="info-icon ms-2">
                    <InfoCircle />
                    <span className="tooltip">More information</span>
                  </span>
                </div>
                <div className="form-floating mb-3 col-12 position-relative d-flex align-items-center">
                  {false && (
                    <Spinner
                      animation="border"
                      role="status"
                      className="position-absolute"
                      style={{ right: "50px" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  )}
                  <Field
                    as="select"
                    name="type"
                    className={`form-select text-dark d-flex align-items-center p-0 ps-3 fs-6 pt-3 ${touched.type && errors.type ? "is-invalid" : ""}`}
                    value={values.type || ""}
                  >
                    <option value={""} disabled>
                      Devices Type
                    </option>
                    <option value={1}>PC</option>
                    <option value={2}>Mobile</option>
                    <option value={0}>Both (PC & Mobile)</option>
                  </Field>
                  <label
                    htmlFor="type"
                    className="form-label fs-7 m-0 pt-3 ps-3"
                  >
                    Devices Type
                  </label>
                </div>
                <div className="form-floating mb-3 col-12 position-relative d-flex align-items-center">
                  {categoryLoading && (
                    <Spinner
                      animation="border"
                      role="status"
                      className="position-absolute"
                      style={{ right: "50px" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  )}
                  <Field
                    as="select"
                    name="templateCategory"
                    className={`form-select text-dark d-flex align-items-center p-0 ps-3 fs-6 pt-3 ${touched.templateCategory && errors.templateCategory ? "is-invalid" : ""}`}
                    value={values.templateCategory || ""}
                  >
                    <option value={""} disabled>
                      Template Category
                    </option>
                    {templateCategoryList?.commonTemplateCategoryList.map(
                      ({ id, title }) => (
                        <option key={id} value={id}>
                          {title}
                        </option>
                      )
                    )}
                  </Field>
                  <label
                    htmlFor="templateCategory"
                    className="form-label fs-7 m-0 pt-3 ps-3"
                  >
                    Template Category
                  </label>
                </div>
                <div className="form-floating mb-3 col-12 position-relative d-flex align-items-center">
                  {userLoading && (
                    <Spinner
                      animation="border"
                      role="status"
                      className="position-absolute"
                      style={{ right: "50px" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  )}
                  <Field
                    as="select"
                    name="user"
                    className={`form-select text-dark d-flex align-items-center p-0 ps-3 fs-6 pt-3 ${touched.user && errors.user ? "is-invalid" : ""}`}
                    value={values.user || ""}
                  >
                    <option value={""} disabled>
                      User
                    </option>
                    <option value={user?.user.id}>{user?.user.email}</option>
                  </Field>
                  <label
                    htmlFor="user"
                    className="form-label fs-7 m-0 pt-3 ps-3"
                  >
                    User
                  </label>
                </div>
                <div className="d-flex flex-column flex-md-row justify-content-between">
                  <button
                    type="button"
                    disabled={createLoading}
                    onClick={() => handleSubmit()}
                    className="btn btn-primary text-white col-12 col-md-4 py-3 mb-4 mb-md-0"
                  >
                    Create
                    {createLoading && (
                      <Spinner
                        animation="border"
                        role="status"
                        className="ms-2"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};
